/**
 * Admin captain create / edit page.
 *
 * Single component drives both routes:
 *   - `/admin/captains/new`         → create mode (no `:id`)
 *   - `/admin/captains/:id/edit`    → edit mode
 *
 * Behavior:
 *  - In edit mode, fetches the existing captain via `ApiClient.getCaptain(id)`
 *    on mount and pre-fills the form (Req 6.7). The `imageUrl` is shown as a
 *    preview but the file input remains optional - the backend keeps the
 *    existing image when no new file is uploaded (Req 6.5).
 *  - Inline per-field validation mirrors the backend rules so invalid input
 *    never reaches the API (Req 5.8): `name` and `jobTitle` are 2-100 Unicode
 *    codepoints (matching backend `validators.isValidName/JobTitle`),
 *    `phoneNumber` matches `^\+?[0-9]{8,15}$` (matching `isValidPhone`), and
 *    the image file is required in create mode and must be JPEG/PNG/WebP up
 *    to 5 MB.
 *  - Submits as `multipart/form-data` so the file upload travels alongside
 *    the text fields (matches `multer` middleware on the backend).
 *  - Server-side errors with a `field` are routed back to that field; other
 *    failures surface as a generic Arabic error message.
 *
 * Validates: Requirements 5.8, 6.7
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ApiClient,
  ApiError,
  UnauthorizedError,
  type Captain,
} from '../api/ApiClient';

import styles from './AdminCaptainFormPage.module.css';

// ---------------------------------------------------------------------------
// Validation constants (mirror backend `validators.js`)
// ---------------------------------------------------------------------------

const TEXT_MIN = 2;
const TEXT_MAX = 100;
const PHONE_PATTERN = /^\+?[0-9]{8,15}$/;
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

// User-facing error messages (Arabic, mirroring the rest of the admin UI).
const NAME_LENGTH_ERROR = 'الاسم يجب أن يكون من 2 إلى 100 حرفاً';
const JOB_LENGTH_ERROR = 'الوظيفة يجب أن تكون من 2 إلى 100 حرفاً';
const PHONE_FORMAT_ERROR = 'رقم الهاتف يجب أن يطابق الصيغة +xxxxxxxx';
const IMAGE_REQUIRED_ERROR = 'يجب رفع صورة';
const IMAGE_MIME_ERROR = 'صيغة الصورة غير مدعومة (JPEG/PNG/WebP فقط)';
const IMAGE_SIZE_ERROR = 'حجم الصورة يتجاوز الحد المسموح (5 ميغابايت)';
const GENERIC_ERROR = 'حدث خطأ، يرجى المحاولة لاحقاً';
const SESSION_EXPIRED_ERROR = 'انتهت الجلسة، يرجى تسجيل الدخول مجدداً';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Counts Unicode codepoints in `s`. Mirrors the backend's
 * `utils/normalize.codepointCount` so surrogate-pair characters (emoji,
 * non-BMP) and Arabic ligatures count consistently on both sides.
 */
function codepointCount(s: string): number {
  return [...s].length;
}

/**
 * Applies the same NFC + trim normalization the backend performs before
 * length checks. Done in the frontend strictly so the inline validation
 * matches the backend's `valid` answer for the exact same input.
 */
function normalize(s: string): string {
  return s.normalize('NFC').trim();
}

interface FieldErrors {
  name?: string;
  jobTitle?: string;
  phoneNumber?: string;
  image?: string;
}

interface ValidateInput {
  name: string;
  jobTitle: string;
  phoneNumber: string;
  imageFile: File | null;
  /** When true the file field is optional (edit mode with existing image). */
  imageOptional: boolean;
}

function validate(input: ValidateInput): FieldErrors {
  const errors: FieldErrors = {};

  const nameLen = codepointCount(normalize(input.name));
  if (nameLen < TEXT_MIN || nameLen > TEXT_MAX) {
    errors.name = NAME_LENGTH_ERROR;
  }

  const jobLen = codepointCount(normalize(input.jobTitle));
  if (jobLen < TEXT_MIN || jobLen > TEXT_MAX) {
    errors.jobTitle = JOB_LENGTH_ERROR;
  }

  if (!PHONE_PATTERN.test(input.phoneNumber.trim())) {
    errors.phoneNumber = PHONE_FORMAT_ERROR;
  }

  if (input.imageFile === null) {
    if (!input.imageOptional) {
      errors.image = IMAGE_REQUIRED_ERROR;
    }
  } else {
    if (!ALLOWED_IMAGE_MIMES.has(input.imageFile.type)) {
      errors.image = IMAGE_MIME_ERROR;
    } else if (input.imageFile.size > IMAGE_MAX_BYTES) {
      errors.image = IMAGE_SIZE_ERROR;
    }
  }

  return errors;
}

/**
 * Maps a server `ApiError` (with `field`) onto our `FieldErrors` map. The
 * backend uses the same field names we use locally except that `imageUrl` is
 * routed to the `image` key in the UI (the user only sees a single "صورة"
 * input, regardless of whether the backend rejects it as `image` or
 * `imageUrl`).
 */
function fieldErrorsFromApi(err: ApiError): FieldErrors {
  if (typeof err.field !== 'string' || err.field.length === 0) {
    return {};
  }
  switch (err.field) {
    case 'name':
      return { name: err.message };
    case 'jobTitle':
      return { jobTitle: err.message };
    case 'phoneNumber':
      return { phoneNumber: err.message };
    case 'image':
    case 'imageUrl':
      return { image: err.message };
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminCaptainFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Mode is locked at mount via the route param; switching routes remounts
  // the component, which resets state cleanly.
  const isEditMode = useMemo(
    () => typeof id === 'string' && id.length > 0,
    [id],
  );

  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [department, setDepartment] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  // ---- Load captain in edit mode --------------------------------------

  useEffect(() => {
    if (!isEditMode || typeof id !== 'string' || id.length === 0) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFormError(null);

    ApiClient.getCaptain(id)
      .then((captain: Captain) => {
        if (cancelled) return;
        setName(captain.name ?? '');
        setJobTitle(captain.jobTitle ?? '');
        setPhoneNumber(captain.phoneNumber ?? '');
        setDepartment(captain.department ?? '');
        setExistingImageUrl(
          typeof captain.imageUrl === 'string' && captain.imageUrl.length > 0
            ? captain.imageUrl
            : null,
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof UnauthorizedError) {
          // ApiClient already redirects on 401; keep state quiet.
          return;
        }
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          setFormError('الموظف غير موجود');
        } else {
          setFormError(GENERIC_ERROR);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  // ---- Field handlers --------------------------------------------------

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleJobTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setJobTitle(e.target.value);
    },
    [],
  );

  const handlePhoneChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value);
    },
    [],
  );

  const handleDepartmentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDepartment(e.target.value);
    },
    [],
  );

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile(file);
    // Create a local object URL for instant preview
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  }, []);

  const handleCancel = useCallback(() => {
    navigate('/admin');
  }, [navigate]);

  // ---- Submit ----------------------------------------------------------

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submitting || loading) return;

      const errors = validate({
        name,
        jobTitle,
        phoneNumber,
        imageFile,
        imageOptional: isEditMode,
      });
      setFieldErrors(errors);
      setFormError(null);

      if (Object.keys(errors).length > 0) {
        // Don't submit when validation fails.
        return;
      }

      const form = new FormData();
      form.set('name', normalize(name));
      form.set('jobTitle', normalize(jobTitle));
      form.set('phoneNumber', phoneNumber.trim());
      if (department.trim() !== '') {
        form.set('department', department.trim());
      }
      if (imageFile !== null) {
        form.set('image', imageFile);
      }

      setSubmitting(true);
      try {
        if (isEditMode && typeof id === 'string') {
          await ApiClient.updateCaptain(id, form);
        } else {
          await ApiClient.createCaptain(form);
        }
        navigate('/admin');
      } catch (err: unknown) {
        if (err instanceof UnauthorizedError) {
          setFormError(SESSION_EXPIRED_ERROR);
          return;
        }
        if (err instanceof ApiError) {
          const mapped = fieldErrorsFromApi(err);
          if (Object.keys(mapped).length > 0) {
            setFieldErrors(mapped);
          } else {
            setFormError(err.message || GENERIC_ERROR);
          }
          return;
        }
        setFormError(GENERIC_ERROR);
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      loading,
      name,
      jobTitle,
      phoneNumber,
      department,
      imageFile,
      isEditMode,
      id,
      navigate,
    ],
  );

  // ---- Render ----------------------------------------------------------

  const heading = isEditMode ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد';

  if (loading) {
    return (
      <div className={styles.page} data-testid="admin-captain-form-page">
        <div className={styles.card}>
          <h1 className={styles.title}>{heading}</h1>
          <div className={styles.loadingBox} role="status" aria-live="polite">
            جاري التحميل...
          </div>
        </div>
      </div>
    );
  }

  const nameErrId = 'admin-captain-name-error';
  const jobErrId = 'admin-captain-job-error';
  const phoneErrId = 'admin-captain-phone-error';
  const imageErrId = 'admin-captain-image-error';

  return (
    <div className={styles.page} data-testid="admin-captain-form-page">
      <div className={styles.card}>
        <h1 className={styles.title}>{heading}</h1>
        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label={heading}
          encType="multipart/form-data"
        >
          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-captain-name">
              الاسم
            </label>
            <input
              id="admin-captain-name"
              className={
                fieldErrors.name
                  ? `${styles.input} ${styles.inputError}`
                  : styles.input
              }
              type="text"
              name="name"
              value={name}
              onChange={handleNameChange}
              aria-invalid={fieldErrors.name !== undefined}
              aria-describedby={
                fieldErrors.name !== undefined ? nameErrId : undefined
              }
              disabled={submitting}
            />
            {fieldErrors.name !== undefined && (
              <p id={nameErrId} className={styles.fieldError} role="alert">
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-captain-job">
              الوظيفة
            </label>
            <input
              id="admin-captain-job"
              className={
                fieldErrors.jobTitle
                  ? `${styles.input} ${styles.inputError}`
                  : styles.input
              }
              type="text"
              name="jobTitle"
              value={jobTitle}
              onChange={handleJobTitleChange}
              aria-invalid={fieldErrors.jobTitle !== undefined}
              aria-describedby={
                fieldErrors.jobTitle !== undefined ? jobErrId : undefined
              }
              disabled={submitting}
            />
            {fieldErrors.jobTitle !== undefined && (
              <p id={jobErrId} className={styles.fieldError} role="alert">
                {fieldErrors.jobTitle}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-captain-phone">
              رقم الهاتف
            </label>
            <input
              id="admin-captain-phone"
              className={
                fieldErrors.phoneNumber
                  ? `${styles.input} ${styles.inputError}`
                  : styles.input
              }
              type="tel"
              name="phoneNumber"
              value={phoneNumber}
              onChange={handlePhoneChange}
              dir="ltr"
              aria-invalid={fieldErrors.phoneNumber !== undefined}
              aria-describedby={
                fieldErrors.phoneNumber !== undefined ? phoneErrId : undefined
              }
              disabled={submitting}
              placeholder="+967777123456"
            />
            {fieldErrors.phoneNumber !== undefined && (
              <p id={phoneErrId} className={styles.fieldError} role="alert">
                {fieldErrors.phoneNumber}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-captain-department">
              القسم / الإدارة <span className={styles.fieldHint}>(اختياري)</span>
            </label>
            <input
              id="admin-captain-department"
              className={styles.input}
              type="text"
              name="department"
              value={department}
              onChange={handleDepartmentChange}
              disabled={submitting}
              placeholder="مثال: المبيعات ، البرمجة ، الإدارة التنفيذية..."
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-captain-image">
              الصورة
              {isEditMode ? (
                <span className={styles.fieldHint}>{' '}(اختياري للتعديل)</span>
              ) : null}
            </label>
            <input
              id="admin-captain-image"
              className={
                fieldErrors.image
                  ? `${styles.fileInput} ${styles.inputError}`
                  : styles.fileInput
              }
              type="file"
              name="image"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              aria-invalid={fieldErrors.image !== undefined}
              aria-describedby={
                fieldErrors.image !== undefined ? imageErrId : undefined
              }
              disabled={submitting}
            />
            {/* Preview of new file selected — shown immediately */}
            {imagePreviewUrl !== null && (
              <div className={styles.newPreviewWrapper}>
                <img
                  className={styles.newPreviewImage}
                  src={imagePreviewUrl}
                  alt="معاينة الصورة الجديدة"
                  data-testid="admin-captain-new-image-preview"
                />
                <span className={styles.newPreviewLabel}>✓ الصورة المختارة</span>
              </div>
            )}
            {existingImageUrl !== null && imageFile === null && (
              <div className={styles.preview}>
                <img
                  className={styles.previewImage}
                  src={existingImageUrl}
                  alt="الصورة الحالية"
                  data-testid="admin-captain-image-preview"
                />
                <span className={styles.fieldHint}>الصورة الحالية</span>
              </div>
            )}
            {fieldErrors.image !== undefined && (
              <p id={imageErrId} className={styles.fieldError} role="alert">
                {fieldErrors.image}
              </p>
            )}
          </div>

          {formError !== null && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting}
            >
              {submitting
                ? 'جاري الحفظ...'
                : isEditMode
                  ? 'حفظ التعديلات'
                  : 'إضافة الموظف'}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={submitting}
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
